import {
  ApiError,
  createTimetable,
  getGroupsExportList,
  getTimetablesCacheKeys,
  GroupType,
  SORT_DIRECTION,
  useGetTimetableStructureQuery,
} from '@schooly/api';
import type { ValidRow, XlsWorkbook } from '@schooly/components/xls-service';
import { exportXls, readXls } from '@schooly/components/xls-service';
import { useAuth } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { SchoolUserRole } from '@schooly/constants';
import { Loading, ModalSmall, NotFoundContent } from '@schooly/style';
import { FC, useCallback, useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFacilities } from '@schooly/hooks/use-facilities';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { useQueryClient } from '@tanstack/react-query';

import { useRouter } from '../../../../context/router/useRouter';
import useSchoolYears from '../../../../hooks/useSchoolYears';
import { getAgeGroupNames } from '../../SchoolTimetableStructures/utils';
import { SCHOOL_YEAR_ID_PARAM, STRUCTURE_ID_PARAM } from '../utils';

import { buildTemplateExportSheets } from './helpers/buildTemplateExportSheets';
import { buildGroupExportColumns, buildGroupExportRows } from './helpers/groupsExport';
import { useTimetableImportMessages } from './helpers/useTimetableImportMessages';
import { buildRoomPathMap, GroupWithCode, isGroupWithCode } from './helpers/buildTimetableSchema';
import {
  INITIAL_STATE,
  getImportableRows,
  importTimetableReducer,
  validateTimetableFile,
} from './helpers/validateTimetableFile';
import { INITIAL_FILE_STATE, importFileReducer } from './helpers/importFileReducer';
import { toSlotAllocations } from './helpers/toSlotAllocations';
import { ImportTimetableContent } from './ImportTimetableContent';
import { InconsistenciesModal } from './InconsistenciesModal';
import { SuccessModal, TryAgainModal } from './ImportFlowModals';
import { SelectSheetModal } from './SelectSheetModal';

export type TimetableImportFile = {
  fileName: string;
  rawBuffer: ArrayBuffer;
};

enum Modal {
  Main,
  Inconsistencies,
  TryAgain,
}

export const ImportTimetableModal: FC = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const { goBack } = useRouter();
  const { showNotification, showError } = useNotifications();
  const { schoolId = '' } = useAuth();
  const { facilities, isLoading: isLoadingFacilities } = useFacilities(
    { schoolId },
    { enabled: !!schoolId },
  );
  const {
    getAgeGroupById,
    getSchoolLevelById,
    schoolLevelsWithAgeGroupsMap,
    isLoading: isLoadingAgeGroups,
  } = useAgeGroups({ schoolId, userType: SchoolUserRole.Student }, { enabled: !!schoolId });
  const msg = useTimetableImportMessages();

  const [fileState, dispatchFile] = useReducer(importFileReducer, INITIAL_FILE_STATE);
  const fileData = fileState.status === 'success' ? fileState.file : undefined;

  const [state, dispatch] = useReducer(importTimetableReducer, INITIAL_STATE);
  const [modal, setModal] = useState<Modal>();

  const queryClient = useQueryClient();

  const isReviewing = state.status === 'reviewing';
  const isImporting = state.status === 'importing';
  const isSuccess = state.status === 'success';
  const isSelectingSheet = state.status === 'selectingSheet';

  const {
    getSchoolYearById,
    isPreviousSchoolYear,
    isLoading: isLoadingSchoolYears,
  } = useSchoolYears();

  const schoolYearId = searchParams.get(SCHOOL_YEAR_ID_PARAM);
  const structureId = searchParams.get(STRUCTURE_ID_PARAM);

  const schoolYear = useMemo(
    () => (schoolYearId ? getSchoolYearById(schoolYearId) : undefined),
    [schoolYearId, getSchoolYearById],
  );

  const { data: structure, isFetching: isFetchingStructure } = useGetTimetableStructureQuery(
    { structureId: structureId ?? '' },
    { enabled: !!structureId, refetchOnMount: 'always' },
  );

  const ageGroupNames = useMemo(
    () =>
      getAgeGroupNames({
        ageGroupIds: structure?.age_group_ids ?? [],
        getAgeGroupById,
        getSchoolLevelById,
        schoolLevelsWithAgeGroupsMap,
      }),
    [structure, getAgeGroupById, getSchoolLevelById, schoolLevelsWithAgeGroupsMap],
  );

  const fetchGroups = useCallback(
    () =>
      getGroupsExportList({
        school_id: schoolId,
        date_from: schoolYear?.start,
        date_to: schoolYear?.end,
        group_types: [GroupType.Subject, GroupType.TutorGroup],
        sort_by: { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
      }).then((data) => data.results),
    [schoolId, schoolYear],
  );
  const handleBack = useCallback(() => dispatch({ type: 'reset' }), []);

  const setImportFile = useCallback(async (file: File) => {
    dispatchFile({ type: 'load' });
    const rawBuffer = await file.arrayBuffer();
    dispatchFile({ type: 'success', file: { fileName: file.name, rawBuffer } });
  }, []);

  const clearImportFile = useCallback(() => {
    dispatchFile({ type: 'clear' });
  }, []);

  const importTimetable = useCallback(
    async (rows: ValidRow[], groups: GroupWithCode[], roomPathMap: Map<string, string>) => {
      if (!structure || !fileData || !schoolYearId) return;

      const allocations = toSlotAllocations({ rows, groups, roomPathMap, structure });
      const fileName = fileData.fileName.replace(/\.xlsx?$/i, '');
      const timestamp = format(new Date(), 'yyyy-MM-dd_HHmm');

      const response = await createTimetable({
        name: `${fileName}_${timestamp}`,
        school_id: schoolId,
        structure_id: structure.id,
        school_year_id: schoolYearId,
        slot_allocations: allocations,
      });

      queryClient.invalidateQueries(
        getTimetablesCacheKeys({ school_year_id: schoolYearId, school_id: schoolId }),
      );

      return response.id;
    },
    [structure, fileData, schoolId, schoolYearId, queryClient],
  );

  const runValidation = useCallback(
    async (workbook: XlsWorkbook, sheetName?: string) => {
      if (!structure) return;

      dispatch({ type: 'validate' });
      try {
        const groups = await fetchGroups();
        const groupsWithCode = groups.filter(isGroupWithCode);
        const roomPathMap = buildRoomPathMap(facilities);

        const result = validateTimetableFile({
          workbook,
          structure,
          groups: groupsWithCode,
          roomPathMap,
          msg,
          sheetName,
        });

        if (!result.ok) {
          setModal(Modal.TryAgain);
          dispatch({ type: 'validationError', reason: result.reason });
          return;
        }

        if (result.result.invalidRows.length > 0) {
          dispatch({
            type: 'review',
            data: { result: result.result, groups: groupsWithCode, roomPathMap },
          });
          setModal(Modal.Inconsistencies);
          return;
        }

        const timetableId = await importTimetable(
          result.result.validRows,
          groupsWithCode,
          roomPathMap,
        );
        if (timetableId) {
          dispatch({
            type: 'success',
            data: {
              timetableId,
              importedRowsCount: result.result.validRows.length,
              notImportedRowsCount: 0,
            },
          });
        }
      } catch (error) {
        showError(error as ApiError);
        handleBack();
      }
    },
    [structure, fetchGroups, facilities, msg, showError, importTimetable, handleBack],
  );

  const handleValidate = useCallback(async () => {
    if (!structure) return;

    if (!fileData) {
      dispatchFile({ type: 'error' });
      showError({ message: formatMessage({ id: 'school-timetables-Import-FileRequired' }) });
      return;
    }

    let workbook: XlsWorkbook;
    try {
      workbook = await readXls(new File([fileData.rawBuffer], fileData.fileName));
    } catch (error) {
      showError({ message: formatMessage({ id: 'import-Timetable-Error' }) });
      console.error(error);
      return;
    }

    if (workbook.sheetNames.length > 1) {
      dispatch({ type: 'selectSheet', data: { workbook, fileName: fileData.fileName } });
      return;
    }

    await runValidation(workbook);
  }, [fileData, structure, showError, formatMessage, runValidation]);

  const handleSheetSelect = useCallback(
    async (sheetName: string) => {
      if (!isSelectingSheet) return;
      await runValidation(state.selectSheetData.workbook, sheetName);
    },
    [isSelectingSheet, state, runValidation],
  );

  const handleImportFromReview = useCallback(async () => {
    if (!isReviewing || !schoolYearId) return;

    const { data } = state;
    dispatch({ type: 'import' });
    try {
      const rows = getImportableRows(data.result);
      const totalRows = data.result.validRows.length + data.result.invalidRows.length;
      const timetableId = await importTimetable(rows, data.groups, data.roomPathMap);

      queryClient.invalidateQueries(
        getTimetablesCacheKeys({ school_year_id: schoolYearId, school_id: schoolId }),
      );
      if (timetableId) {
        dispatch({
          type: 'success',
          data: {
            timetableId,
            importedRowsCount: rows.length,
            notImportedRowsCount: totalRows - rows.length,
          },
        });
      }
    } catch (error) {
      showError(error as ApiError);
      dispatch({ type: 'error' });
    }
  }, [state, importTimetable, showError, isReviewing, schoolYearId, schoolId, queryClient]);

  const handleExportGroups = useCallback(async () => {
    if (!schoolId || !schoolYear) return;

    try {
      const groups = await fetchGroups();
      const columns = buildGroupExportColumns(msg);
      const rows = buildGroupExportRows({ groups, facilities, msg });

      await exportXls(
        [{ name: msg.fileNameGroups, rows, columns }],
        `${msg.fileNameGroups} ${schoolYear.name}`,
      );

      showNotification({
        type: 'success',
        message: formatMessage({ id: 'school-timetables-Import-ExportGroupsSuccess' }),
      });
    } catch {
      showError({ message: formatMessage({ id: 'error-ExportProblems' }) });
    }
  }, [
    facilities,
    fetchGroups,
    formatMessage,
    msg,
    schoolId,
    schoolYear,
    showError,
    showNotification,
  ]);

  const handleExportTemplate = useCallback(async () => {
    if (!structure || !schoolYear) return;

    try {
      const sheets = buildTemplateExportSheets({ structure, ageGroupNames, msg });

      await exportXls(sheets, `${msg.fileNameTemplate}. ${structure.name}. ${schoolYear.name}`);

      showNotification({
        type: 'success',
        message: formatMessage({ id: 'school-timetables-Import-DownloadTemplateSuccess' }),
      });
    } catch {
      showError({ message: formatMessage({ id: 'error-ExportProblems' }) });
    }
  }, [ageGroupNames, formatMessage, msg, schoolYear, showError, showNotification, structure]);

  const handleClose = useCallback(() => navigate('/settings/timetables'), [navigate]);

  if (!schoolYearId || !structureId) {
    navigate('/settings/timetables/new');
    return null;
  }

  const isLoading =
    isLoadingSchoolYears || isFetchingStructure || isLoadingFacilities || isLoadingAgeGroups;

  if (isLoading) {
    return (
      <ModalSmall open onClose={handleClose}>
        <Loading />
      </ModalSmall>
    );
  }

  const notFound = !schoolYear || !structure || isPreviousSchoolYear(schoolYear);

  if (notFound) {
    return (
      <ModalSmall open onClose={handleClose}>
        <NotFoundContent goBack={goBack} />
      </ModalSmall>
    );
  }

  if (modal === Modal.Inconsistencies && (isReviewing || isImporting)) {
    return (
      <InconsistenciesModal
        validationResult={state.data.result}
        onBack={handleBack}
        onImport={handleImportFromReview}
        isImporting={state.status === 'importing'}
        onClose={handleClose}
      />
    );
  }

  if (modal === Modal.TryAgain && state.status === 'validationError') {
    return (
      <TryAgainModal
        onClose={handleClose}
        onTryAgain={() => {
          setModal(undefined);
          handleBack();
        }}
        fileName={fileData?.fileName ?? ''}
        columnNames={[msg.columnDay, msg.columnPeriod, msg.columnGroup]}
        reason={state.reason}
      />
    );
  }

  if (isSuccess) {
    return (
      <SuccessModal
        onClose={() => navigate('/settings/timetables')}
        fileName={fileData?.fileName ?? ''}
        importedRowsCount={state.data.importedRowsCount}
        notImportedRowsCount={state.data.notImportedRowsCount}
        onViewTimetable={() => navigate(`/settings/timetables/${state.data.timetableId}/edit`)}
      />
    );
  }

  return (
    <>
      <ImportTimetableContent
        structure={structure}
        schoolYear={schoolYear}
        ageGroupNames={ageGroupNames}
        fileState={fileState}
        isValidating={state.status === 'validating'}
        onSetFile={setImportFile}
        onClearFile={clearImportFile}
        onExportGroups={handleExportGroups}
        onExportTemplate={handleExportTemplate}
        onValidateImport={handleValidate}
        onClose={handleClose}
      />

      {isSelectingSheet && (
        <SelectSheetModal
          fileName={state.selectSheetData.fileName}
          sheetNames={state.selectSheetData.workbook.sheetNames}
          onSelect={handleSheetSelect}
          onClose={handleBack}
        />
      )}
    </>
  );
};
